/**
 * Datas financeiras (competência, vencimento, pagamento) são strings "YYYY-MM-DD" sem fuso.
 * Nunca converta para Date local para exibir: isso desloca o dia em fusos negativos.
 * Eventos de auditoria são timestamps UTC exibidos no fuso da empresa.
 */

export type ISODate = string; // YYYY-MM-DD
export type Competence = string; // YYYY-MM

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const COMPETENCE = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function isISODate(value: unknown): value is ISODate {
  if (typeof value !== 'string') return false;
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= daysInMonth(y, mo);
}

export function isCompetence(value: unknown): value is Competence {
  return typeof value === 'string' && COMPETENCE.test(value);
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Data de hoje no fuso informado, como YYYY-MM-DD. */
export function todayInTimeZone(timeZone: string, now: Date = new Date()): ISODate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function addDays(date: ISODate, days: number): ISODate {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

/** Soma meses preservando o dia; se o mês não tiver o dia, usa o último dia do mês. */
export function addMonths(date: ISODate, months: number): ISODate {
  const [y, m, d] = date.split('-').map(Number);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const nd = Math.min(d, daysInMonth(ny, nm));
  return `${ny}-${String(nm).padStart(2, '0')}-${String(nd).padStart(2, '0')}`;
}

export function competenceOf(date: ISODate): Competence {
  return date.slice(0, 7);
}

export function addCompetence(c: Competence, months: number): Competence {
  return competenceOf(addMonths(`${c}-01`, months));
}

/** Primeiro e último dia de uma competência. */
export function competenceRange(c: Competence): { from: ISODate; to: ISODate } {
  const [y, m] = c.split('-').map(Number);
  return { from: `${c}-01`, to: `${c}-${String(daysInMonth(y, m)).padStart(2, '0')}` };
}

/** Data em uma competência com o dia desejado (ajustado ao último dia do mês). */
export function dateInCompetence(c: Competence, day: number): ISODate {
  const [y, m] = c.split('-').map(Number);
  const d = Math.min(Math.max(1, day), daysInMonth(y, m));
  return `${c}-${String(d).padStart(2, '0')}`;
}

export function diffDays(from: ISODate, to: ISODate): number {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = to.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000);
}

/** "2026-11-04" → "04/11/2026". null → "—". */
export function formatDate(date: ISODate | null | undefined): string {
  if (!date) return '—';
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
}

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MONTHS_LONG = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/** "2026-11" → "nov/2026" */
export function formatCompetence(c: Competence | null | undefined, long = false): string {
  if (!c) return '—';
  const [y, m] = c.split('-').map(Number);
  return long ? `${MONTHS_LONG[m - 1]} de ${y}` : `${MONTHS[m - 1]}/${y}`;
}

/** Timestamp UTC → data/hora no fuso da empresa. */
export function formatDateTime(iso: string | Date | null | undefined, timeZone: string): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Converte "dd/mm/aaaa" ou "aaaa-mm-dd" para ISO; inválido → null. */
export function parseDateInput(value: string | null | undefined): ISODate | null {
  if (!value) return null;
  const v = value.trim();
  if (isISODate(v)) return v;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
  if (!m) return null;
  const iso = `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return isISODate(iso) ? iso : null;
}
